import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut,
} from 'firebase/auth'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '@/lib/firebase'
import { createLogger } from '@/utils/logger'
import type { RegisterData } from '@/types/api'

const log = createLogger('Register')

export async function login(email: string, password: string) {
  return signInWithEmailAndPassword(auth, email, password)
}

export async function register(data: RegisterData) {
  const { email, password, name, churchName, churchDescription } = data

  // Create Firebase Auth user
  log.debug('creating auth user...')
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  const user = credential.user
  log.debug('auth user created — uid:', user.uid)

  // Set display name
  await updateProfile(user, { displayName: name })

  // Create church document
  const churchId = user.uid + '_church'
  const churchRef = doc(db, 'churches', churchId)
  log.debug('creating church doc — id:', churchId)
  await setDoc(churchRef, {
    name: churchName,
    description: churchDescription || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    isDemo: false,
  })
  log.debug('church doc created OK')

  // Create user document
  const userRef = doc(db, 'users', user.uid)
  log.debug('creating user doc — churchId:', churchRef.id)
  await setDoc(userRef, {
    name,
    email,
    role: 'admin',
    churchId: churchRef.id,
    notificationPreferences: {
      taskAssignments: true,
      taskReminders: true,
      email: true,
      push: true,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  log.debug('user doc created OK')

  return credential
}

export async function resetPassword(email: string) {
  return sendPasswordResetEmail(auth, email)
}

export async function joinChurch(
  email: string,
  password: string,
  name: string,
  churchId: string,
) {
  const credential = await createUserWithEmailAndPassword(auth, email, password)
  const user = credential.user

  await updateProfile(user, { displayName: name })

  const userRef = doc(db, 'users', user.uid)
  await setDoc(userRef, {
    name,
    email,
    role: 'user',
    churchId,
    notificationPreferences: {
      taskAssignments: true,
      taskReminders: true,
      email: true,
      push: true,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })

  return credential
}

export async function logout() {
  return signOut(auth)
}
